<script setup lang="ts">
import {
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogRoot,
  AlertDialogTitle,
} from 'reka-ui';
import { computed, toRef, useAttrs, type VNode, watchEffect } from 'vue';

import { KaButton } from '@/kasten-components/components/KaButton';
import { useDataTestid, useDialogTriggerFocus } from '@/kasten-components/composables';
import type { VueClass } from '@/kasten-components/types';
import { cnMerge } from '@/utils/utils.js';

import {
  modalButtonsGridClasses,
  modalConfirmBodyClasses,
  modalContainerClasses,
  modalContentClasses,
  modalContentTransition,
  modalDescriptionClasses,
  modalOverlayClasses,
  modalOverlayTransition,
  modalTitleClasses,
} from '../KaModal/modal-variants';

defineOptions({
  inheritAttrs: false,
});

export interface KaAlertModalProps {
  open: boolean;
  title?: string;
  description?: string;
  confirmText?: string;
  dismissText?: string;
  /** Destructive (red) confirm button. Defaults to `true` since alert dialogs gate destructive actions. */
  danger?: boolean;
  zIndexClass?: VueClass;
  width?: 'full' | 'auto';
  modalClasses?: VueClass;
  isPending?: boolean;
  submitBtnFormAttr?: string;
  overlayClasses?: VueClass;
  /**
   * Element to return focus to when the dialog closes. Pass this when the dialog
   * is opened from a transient trigger (a menu item, popover, etc.) that unmounts
   * before the dialog closes — otherwise reka's default restore target is gone and
   * focus falls through to `<body>`. When omitted, the element focused at open time
   * is captured as a fallback. See K10-36133.
   */
  triggerEl?: HTMLElement | null;
}

const props = withDefaults(defineProps<KaAlertModalProps>(), {
  title: undefined,
  description: '',
  confirmText: 'Confirm',
  dismissText: 'Cancel',
  danger: true,
  zIndexClass: 'z-50',
  width: 'full',
});

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'confirm', value: MouseEvent): void;
  (e: 'close'): void;
}>();

const attrs = useAttrs();
const slots = defineSlots<{
  content(): VNode;
  description(): VNode;
  buttons(): VNode;
}>();

const isDanger = computed(() => !!props.danger);
const hasDescription = computed(() => !!props.description || !!slots.description);

if (import.meta.env.DEV) {
  watchEffect(() => {
    if (!props.title) {
      // eslint-disable-next-line no-console
      console.warn('[KaAlertModal] Provide a `title` prop for accessibility (aria-labelledby).');
    }
    if (!props.description && !slots.description) {
      // eslint-disable-next-line no-console
      console.warn(
        '[KaAlertModal] Provide either a `description` prop or a `#description` slot — alert dialogs announce the description immediately (aria-describedby).',
      );
    }
  });
}

const { dataTestid, suffix } = useDataTestid((attrs.id as string) || 'confirmation-dialog');

const { handleCloseAutoFocus } = useDialogTriggerFocus(
  toRef(props, 'open'),
  toRef(props, 'triggerEl'),
);

const internalOpen = computed({
  get: () => props.open,
  set: (value: boolean) => {
    emit('update:open', value);
    if (!value) {
      emit('close');
    }
  },
});
</script>
<template>
  <AlertDialogRoot v-model:open="internalOpen">
    <AlertDialogPortal>
      <Transition v-bind="modalOverlayTransition">
        <AlertDialogOverlay
          v-if="props.open"
          :class="cnMerge([modalOverlayClasses, props.zIndexClass, props.overlayClasses])"
        />
      </Transition>

      <div
        :class="
          cnMerge([
            modalContainerClasses,
            props.zIndexClass,
            // Disable pointer events when modal is closed so the container can stay
            // mounted for the leave transition without blocking interactions.
            { 'pointer-events-none': !props.open },
          ])
        "
      >
        <Transition v-bind="modalContentTransition">
          <AlertDialogContent
            v-if="props.open"
            aria-modal="true"
            :data-testid="dataTestid"
            @close-auto-focus="handleCloseAutoFocus"
            :class="[
              modalContentClasses,
              'sm:max-w-lg',
              { 'sm:w-full': props.width === 'full' },
              props.modalClasses,
            ]"
          >
            <div :class="modalConfirmBodyClasses">
              <AlertDialogTitle :class="modalTitleClasses">
                {{ props.title }}
              </AlertDialogTitle>

              <AlertDialogDescription v-if="hasDescription" :class="modalDescriptionClasses">
                <slot name="description">{{ props.description }}</slot>
              </AlertDialogDescription>

              <slot name="content" />

              <div class="mt-5">
                <slot name="buttons">
                  <div :class="modalButtonsGridClasses">
                    <KaButton
                      variant="primary"
                      :danger="isDanger"
                      :data-testid="suffix('confirm')"
                      :loading="isPending"
                      :use-focus-visible="false"
                      :type="submitBtnFormAttr ? 'submit' : 'button'"
                      :form="submitBtnFormAttr"
                      class="sm:col-start-2"
                      @click="emit('confirm', $event)"
                    >
                      {{ props.confirmText }}
                    </KaButton>
                    <AlertDialogCancel as-child>
                      <KaButton
                        :data-testid="suffix('cancel')"
                        :use-focus-visible="false"
                        class="sm:col-start-1"
                      >
                        {{ props.dismissText }}
                      </KaButton>
                    </AlertDialogCancel>
                  </div>
                </slot>
              </div>
            </div>
          </AlertDialogContent>
        </Transition>
      </div>
    </AlertDialogPortal>
  </AlertDialogRoot>
</template>
